/**
 * The instrument pool and the market groups it is played and filtered by, in
 * shared runtime code so one list serves both tiers: the pipeline fetches and
 * builds from it (re-exported by `dataPipeline/config.ts`, the precedent
 * `PRESETS` set), and the settings strip groups its market chips from it. The
 * UI never reaches into `dataPipeline/`; the dependency runs the other way.
 *
 * Stored Round ids embed the instrument id (`"CL.F:2019-03-04"`), so an id here
 * is permanent: renaming one orphans every banked Round that references it.
 */

/** A market group: the coarse bucket a player filters by. */
export type MarketGroup =
  | "indices"
  | "stocks"
  | "forex"
  | "crypto"
  | "metals"
  | "energy";

/** The groups in display order, with the label the chip row shows. */
export const MARKET_GROUPS: { id: MarketGroup; label: string }[] = [
  { id: "indices", label: "Indices" },
  { id: "stocks", label: "Stocks" },
  { id: "forex", label: "Forex" },
  { id: "crypto", label: "Crypto" },
  { id: "metals", label: "Metals" },
  { id: "energy", label: "Energy" },
];

/** An instrument in the pool: its stored id, reveal-only name, Yahoo symbol. */
export type Instrument = {
  /**
   * Stored id. Futures use the `.F` continuous-future form ("CL.F"); a cash
   * equity is its plain ticker ("AAPL"). Reveal-only.
   */
  id: string;
  /** Player-facing name, shown only at Reveal. */
  name: string;
  /** Yahoo Finance symbol (`=F` form for futures, see ADR-0001). Build-time only. */
  yahooSymbol: string;
  /** Which market group the chip row files this instrument under. */
  group: MarketGroup;
};

/** One stock entry: for a cash equity the stored id is the ticker itself. */
function stock(ticker: string, name: string): Instrument {
  return {
    id: ticker,
    name: `${name} (${ticker})`,
    yahooSymbol: ticker,
    group: "stocks",
  };
}

export const INSTRUMENTS: Instrument[] = [
  { id: "ES.F", name: "E-mini S&P 500 (ES)", yahooSymbol: "ES=F", group: "indices" },
  { id: "NQ.F", name: "E-mini Nasdaq 100 (NQ)", yahooSymbol: "NQ=F", group: "indices" },

  stock("AAPL", "Apple"),
  stock("MSFT", "Microsoft"),
  stock("NVDA", "Nvidia"),
  stock("GOOGL", "Alphabet"),
  stock("AMZN", "Amazon"),
  stock("META", "Meta"),
  stock("TSLA", "Tesla"),
  stock("BRK-B", "Berkshire Hathaway"),
  stock("JPM", "JPMorgan"),
  stock("NFLX", "Netflix"),

  { id: "6E.F", name: "Euro FX (6E)", yahooSymbol: "6E=F", group: "forex" },
  { id: "6J.F", name: "Japanese Yen (6J)", yahooSymbol: "6J=F", group: "forex" },
  { id: "6B.F", name: "British Pound (6B)", yahooSymbol: "6B=F", group: "forex" },
  { id: "6A.F", name: "Australian Dollar (6A)", yahooSymbol: "6A=F", group: "forex" },
  { id: "6C.F", name: "Canadian Dollar (6C)", yahooSymbol: "6C=F", group: "forex" },
  { id: "6S.F", name: "Swiss Franc (6S)", yahooSymbol: "6S=F", group: "forex" },

  { id: "BTC.F", name: "Bitcoin (BTC)", yahooSymbol: "BTC=F", group: "crypto" },
  { id: "ETH.F", name: "Ether (ETH)", yahooSymbol: "ETH=F", group: "crypto" },

  { id: "GC.F", name: "Gold (GC)", yahooSymbol: "GC=F", group: "metals" },
  { id: "SI.F", name: "Silver (SI)", yahooSymbol: "SI=F", group: "metals" },
  { id: "HG.F", name: "Copper (HG)", yahooSymbol: "HG=F", group: "metals" },

  { id: "CL.F", name: "Crude Oil (CL)", yahooSymbol: "CL=F", group: "energy" },
  { id: "NG.F", name: "Natural Gas (NG)", yahooSymbol: "NG=F", group: "energy" },
];

/** The pool's instruments in that group, in pool order. */
export function instrumentsInGroup(group: MarketGroup): Instrument[] {
  return INSTRUMENTS.filter((instrument) => instrument.group === group);
}

/**
 * The chip label for an instrument id: the bare ticker, with the `.F`
 * continuous-future suffix dropped (a stock id is already bare).
 */
export function tickerOf(instrumentId: string): string {
  return instrumentId.replace(/\.F$/, "");
}
